import { useState } from 'react';
import { AGENTS, listedAgents, type Agent } from '@/mocks/chat';
import { allowLocalModels } from '@/platform';

type Props = {
  value: Agent['id'];
  onChange: (id: Agent['id']) => void;
  testId?: string;
};

export default function AgentPicker({ value, onChange, testId }: Props) {
  const [open, setOpen] = useState(false);
  const options = listedAgents(allowLocalModels());
  const current = AGENTS.find((a) => a.id === value) ?? options[0] ?? AGENTS[0];

  return (
    <div className="relative">
      <button
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="whitespace-nowrap flex items-center gap-2 bg-surface hover:bg-surface2 border border-border rounded-md px-2.5 py-1.5 text-xs font-medium text-textp"
      >
        <span
          className="w-5 h-5 rounded flex items-center justify-center shrink-0"
          style={{ background: `${current.color}22`, color: current.color }}
        >
          <i className={`${current.icon} text-xs`} />
        </span>
        {current.name}
        <i className="ri-arrow-down-s-line text-texts text-xs" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 top-full left-0 mt-1 w-56 bg-surface border border-border rounded-md py-1 shadow-xl">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-texts">Pick an agent</div>
            {options.map((a) => (
              <button
                key={a.id}
                data-testid={`agent-option-${a.id}`}
                onClick={() => {
                  onChange(a.id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-surface2 ${
                  a.id === value ? 'text-textp' : 'text-texts'
                }`}
              >
                <span
                  className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                  style={{ background: `${a.color}22`, color: a.color }}
                >
                  <i className={`${a.icon} text-xs`} />
                </span>
                <span className="flex-1">
                  <span className="block text-textp">{a.name}</span>
                  <span className="block text-[10px] text-texts">{a.description}</span>
                </span>
                {a.id === value && <i className="ri-check-line text-accent" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
